/**
 * Inline "create automation rule" affordance for invoice field edits.
 *
 * After a user maps a QBO/Xero/Excel field on an invoice (header vendor /
 * customer, or a line's category / service / tax), we surface this small chip
 * directly under that field: `+ Always do this for "<vendor>"`. Clicking it
 * opens the invoice automation-rules drawer (TagRegexesInvoice) PRE-FILLED with
 * a brand new (unsaved) rule whose condition is the invoice's extracted vendor
 * and whose action sets the field the user just edited to the value they chose.
 *
 * Mirrors ApplyToAllLineItems in placement/affordance: not a modal, appears only
 * right after an edit, dismissible. Purely presentational + a single dispatch —
 * the parent decides when to show it and supplies the seed (condition vendor +
 * action target/value/label). The user reviews and Saves in the drawer; we never
 * auto-save the rule.
 *
 * This does NOT touch vendor/customer memory — memory remains the implicit
 * layer; an explicit rule created here takes precedence over it in the engine.
 */
import React from "react";
import { useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  setShowModalInvoiceRules,
  setPendingInvoiceRuleSeed,
  InvoiceRuleSeed,
  InvoiceRuleSeedTarget,
} from "src/redux/reducers/InvoiceManager";
import { DocType } from "src/docuclipper/DocuclipperTypes";

type Props = {
  /** The invoice's extracted vendor/customer used as the rule condition. */
  conditionVendor: string;
  /** Which field the user just edited (becomes the rule action target). */
  target: InvoiceRuleSeedTarget;
  /** The id/value the field was set to (the rule action value). */
  value: string;
  /** Human-readable label for the value (shown in the drawer + persisted). */
  valueLabel?: string;
  /** docType the field belongs to — guards which drawer consumes the seed. */
  docType: DocType;
  /** Integration the field belongs to (null for free-form Excel). */
  integrationId?: number | null;
  /** Whether the condition is on the vendor (payables) or customer (receivables). */
  conditionField?: "vendor" | "customer";
  /** Optional cy hook for tests. */
  dataCy?: string;
};

const CreateRuleFromFieldChip: React.FC<Props> = ({
  conditionVendor,
  target,
  value,
  valueLabel,
  docType,
  integrationId = null,
  conditionField = "vendor",
  dataCy,
}) => {
  const dispatch = useDispatch();

  // Without an extracted vendor/customer there's no condition to key the rule
  // on, and without a value there's no action — show nothing.
  if (!conditionVendor || !value) return null;

  const handleClick = () => {
    const seed: InvoiceRuleSeed = {
      ifConditions: [
        {
          field: conditionField,
          operator: "containsAny",
          value: { keywords: [conditionVendor] },
        },
      ],
      thenSet: [
        {
          target,
          value,
          valueLabel: valueLabel || "",
        },
      ],
      docType,
      integrationId,
    };
    dispatch(setPendingInvoiceRuleSeed(seed));
    dispatch(setShowModalInvoiceRules(true));
  };

  return (
    <div
      data-cy={dataCy || "create-rule-from-field"}
      className="d-inline-flex align-items-center mt-1"
      style={{
        fontSize: "0.75rem",
        lineHeight: 1.2,
        background: "#fef7ec",
        border: "1px solid #f4d9a6",
        borderRadius: "4px",
        padding: "2px 6px",
        color: "#8a5a00",
        maxWidth: "100%",
      }}
    >
      <FontAwesomeIcon icon="magic" className="mr-1" />
      <button
        type="button"
        onClick={handleClick}
        className="btn btn-link p-0 border-0 align-baseline"
        style={{
          fontSize: "0.75rem",
          color: "#8a5a00",
          fontWeight: 600,
          textDecoration: "underline",
        }}
        title="Create an automation rule from this field"
      >
        Always do this for "{conditionVendor}"
      </button>
    </div>
  );
};

export default CreateRuleFromFieldChip;
